import { useEffect, useRef } from 'react';
import { env } from '@noor/config';
import { readAuthToken } from '@noor/api';
import {
  adminAuthFrame,
  adminPingFrame,
  readAdminSocketFrame,
  type AdminSocketChatAction,
} from './protocol';

/** cpanel's keepalive period. */
const PING_INTERVAL_MS = 10_000;

/** Reconnect backoff bounds. */
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

/**
 * The cpanel admin socket (`VITE_SOCKET_URL`): connect, answer `waiting_auth`
 * with the access token, ping once authenticated, hand `refresh` pushes to
 * `onRefresh` and the chat pushes to `onChatEvent`. Mirrors the merchant panel's `useLegacySocket` lifecycle — one
 * socket per hook instance (no module-level singleton), and an exponential
 * reconnect that does not give up (cpanel stopped after 3 tries, leaving a
 * stale list that looks live). `auth_failed` is terminal: retrying a rejected
 * token only loops.
 */
export function useAdminSocket({
  enabled,
  onRefresh,
  onChatEvent,
  onOffer,
}: {
  enabled: boolean;
  onRefresh: () => void;
  onOffer?: (content: Record<string, unknown>) => void;
  onChatEvent?: (action: AdminSocketChatAction) => void;
}): void {
  const onOfferRef = useRef(onOffer);
  const onRefreshRef = useRef(onRefresh);
  const onChatEventRef = useRef(onChatEvent);
  useEffect(() => {
    onOfferRef.current = onOffer;
    onRefreshRef.current = onRefresh;
    onChatEventRef.current = onChatEvent;
  }, [onRefresh, onChatEvent, onOffer]);

  useEffect(() => {
    if (!enabled || !env.VITE_SOCKET_URL) return;

    let socket: WebSocket | null = null;
    let pingTimer: ReturnType<typeof setInterval> | undefined;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    let disposed = false;

    const clearPing = () => {
      if (pingTimer) clearInterval(pingTimer);
      pingTimer = undefined;
    };

    const connect = () => {
      if (disposed || !readAuthToken()) return;
      socket = new WebSocket(env.VITE_SOCKET_URL);

      socket.onmessage = (event: MessageEvent<string>) => {
        if (disposed) return;
        const action = readAdminSocketFrame(event.data);
        switch (action.kind) {
          case 'authenticate': {
            // Re-read: the handshake can outlive a token refresh.
            const token = readAuthToken();
            if (token) socket?.send(adminAuthFrame(token));
            break;
          }
          case 'authenticated':
            attempt = 0;
            clearPing();
            pingTimer = setInterval(() => {
              if (socket?.readyState === WebSocket.OPEN) socket.send(adminPingFrame());
            }, PING_INTERVAL_MS);
            break;
          case 'auth-failed':
            disposed = true;
            socket?.close();
            break;
          case 'courier-offer':
            onOfferRef.current?.(action.content);
            break;
          case 'refresh':
            onRefreshRef.current();
            break;
          case 'chat-message':
          case 'new-chat':
          case 'chat-status':
          case 'messages-seen':
            onChatEventRef.current?.(action);
            break;
          case 'ignore':
            break;
        }
      };

      socket.onclose = () => {
        clearPing();
        socket = null;
        if (disposed) return;
        const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS);
        attempt += 1;
        reconnectTimer = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      disposed = true;
      clearPing();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (socket) {
        // Drop the handler before closing, or teardown schedules a reconnect.
        socket.onclose = null;
        socket.close();
      }
    };
  }, [enabled]);
}
